<template>
  <span v-if="props.anchor" :id="props.anchor" class="anchor"></span>
  <div class="card" v-bind="$attrs">
    <div class="card-header d-flex justify-content-between bg-primary">
      <h4 class="text-uppercase mb-0">Options</h4>
      <button class="btn btn-link link-dark p-0 link-underline-opacity-0 link-underline-opacity-100-hover" @click="addOption()"><i class="fa-solid fa-plus pe-1"></i>Ajouter une option</button>
    </div>
    <div class="card-body">
      <div v-if="options.length === 0" class="text-muted">
        Aucune option
      </div>
      <div v-else v-for="(option,idx) in options" :key="option.key" class="card mb-3">
        <span :id="`options[${idx}]`" class="anchor"></span>
        <div class="card-header d-flex justify-content-between bg-primary bg-opacity-25">
          <h4 class="text-uppercase mb-0">{{option.value?.name.length > 0 ? option.value?.name : `Option ${idx + 1}`}}</h4>
          <button class="btn btn-link link-dark p-0" @click="removeOption(idx)"><i class="fa-regular fa-trash-can fa-lg"></i></button>
        </div>
        <div class="card-body">
          <div class="row mb-3">
            <div class="col-12">
              <TextInputComponent
                :value-key="`options[${idx}].name`"
                label="Nom groupe"
                placeholder="Nom groupe"
                :required="true"
              />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-12 col-md-4">
              <TextInputComponent
                :value-key="`options[${idx}].linkText`"
                label="Texte avant le lien"
                placeholder="Texte avant le lien"
                :required="false"
              />
            </div>
            <div class="col-12 col-md-4">
              <TextInputComponent
                :value-key="`options[${idx}].link`"
                label="Lien"
                placeholder="Lien"
                :required="false"
              />
            </div>
            <div class="col-12 col-md-4">
              <TextInputComponent
                :value-key="`options[${idx}].linkLabel`"
                label="Texte du lien"
                placeholder="Texte du lien"
                :required="false"
              />
            </div>
          </div>

          <AdminTravelEditOptionActivity
            class="mb-3"
            :value-key="`options[${idx}].activities`"
            v-model:validation-schema="activitiesValidationSchema"
          />
        </div>

      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import { useFieldArray } from 'vee-validate';
import {IdNotDefined,ScrollToId } from '@/compositions/helper'
import TextInputComponent from '@/components/common/TextInputComponent.vue';
import AdminTravelEditOptionActivity from '@/views/admin/travel/AdminTravelEditOptionActivity.vue';

import * as zod from 'zod';

const validationSchema = defineModel<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>('validation-schema',{ required: false, default: () => ({ shape: {} }) });
const activitiesValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} });

const props = defineProps<{
  anchor?: string
}>();

const { remove: removeOption, push: pushOption, fields: options } = useFieldArray<TravelDbOption>(() => 'options');

const addOption = () => {
  pushOption(<TravelDbOption>{
    id: IdNotDefined,
    name: `Option ${(options?.value.length ?? 0) + 1}`,
  });
  ScrollToId(`options[${(options?.value.length ?? 1) - 1}]`);
}

const mergedSchema = computed(() => {
  return {
    shape: {
      options: zod.array(zod.object({
        id: zod.number().nonnegative(),
        name: zod.string().min(1, 'Le nom de l\'option est obligatoire'),
        link: zod.union([zod.string().url('Le lien de l\'option n\'est pas valide').nullish(), zod.literal("")]),
        linkLabel: zod.union([zod.string().nullish(), zod.literal("")]),
        linkText: zod.union([zod.string().nullish(), zod.literal("")]),
        ...zod.object(activitiesValidationSchema.value.shape).shape,
      })).optional()
    },
    anchors: {
      "options[\\d].id": "self",
      "options[\\d].name": "self",
      "options[\\d].link": "self",
      "options[\\d].linkLabel": "self",
      "options[\\d].linkText": "self",
      ...activitiesValidationSchema.value.anchors
    }
  }
});

onMounted(() => {
  validationSchema.value = mergedSchema.value
});

watch(activitiesValidationSchema, async () => {
  validationSchema.value = mergedSchema.value
});
</script>
